import { useCallback } from "react";
import { Texture } from "pixi.js";
import { TextureCache, TextureCacheOptions } from "../lib/texture/TextureCache";

export type { TextureCacheOptions };

// Global cache instance shared across all components
let globalTextureCache: TextureCache | null = null;

/**
 * A React hook for creating a shared, size-limited texture cache for PixiJS.
 * This hook is designed to prevent race conditions and duplicate texture loading
 * when used by multiple components concurrently.
 *
 * @param options - Configuration for the cache. Only used if the cache hasn't been initialized yet.
 * @param options.maxSize - The maximum number of textures to store in the cache. Defaults to 100.
 * @param options.crossOrigin - The CORS setting for images. Use "anonymous" (default) for WebGL compatibility, "" to disable CORS, or "use-credentials" for credentialed requests. Note: WebGL requires CORS to be enabled to avoid tainted canvas errors.
 * @returns An object with functions to interact with the cache: loadTexture, getTexture, clearCache, getCurrentSize, getMaxSize.
 */
export function useTextureCache(options: TextureCacheOptions = {}) {
  // Initialize the global cache instance only once
  if (globalTextureCache === null) {
    globalTextureCache = new TextureCache(options);
  }

  const loadTexture = useCallback((url: string): Promise<Texture> => {
    return globalTextureCache!.loadTexture(url);
  }, []);

  const loadRoundedTexture = useCallback(
    (url: string, radius: number): Promise<Texture> => {
      return globalTextureCache!.loadRoundedTexture(url, radius);
    },
    []
  );

  const getTexture = useCallback((url: string): Texture | null => {
    return globalTextureCache!.getTexture(url);
  }, []);

  const clearCache = useCallback(() => {
    globalTextureCache!.clearCache();
  }, []);

  const getCurrentSize = useCallback((): number => {
    return globalTextureCache!.getCurrentSize();
  }, []);

  const getMaxSize = useCallback((): number => {
    return globalTextureCache!.getMaxSize();
  }, []);

  return {
    loadTexture,
    loadRoundedTexture,
    getTexture,
    clearCache,
    getCurrentSize,
    getMaxSize,
  };
}
